// src/store/slices/tableCacheSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface TableCacheItem<T> {
  data: T[];
  total: number;
  search: string;
  page: number;
  pageSize: number;
  sortField?: keyof T;
  sortOrder?: 'ascend' | 'descend';
  lastFetched?: number;
  filter?: string;
}

// 👇 dùng Record thay vì {[pageKey: string]: TableCacheItem<unknown>}
export type TableCacheState = Record<string, TableCacheItem<unknown>>;

const initialState: TableCacheState = {};

const tableCacheSlice = createSlice({
  name: 'tableCache',
  initialState,
  reducers: {
    setTableData: <T>(
      state: TableCacheState,
      action: PayloadAction<{
        pageKey: string;
        data: T[];
        total: number;
      }>,
    ) => {
      const { pageKey, data, total } = action.payload;

      const existing = state[pageKey] as TableCacheItem<T> | undefined;

      const updated: TableCacheItem<T> = {
        ...(existing ?? {
          search: '',
          page: 1,
          pageSize: 10,
          data: [],
          total: 0,
          filter: '',
        }),
        data,
        total,
        lastFetched: Date.now(),
      };

      state[pageKey] = updated as TableCacheItem<unknown>;
    },

    setTableParams: <T>(
      state: TableCacheState,
      action: PayloadAction<{
        pageKey: string;
        params: Partial<TableCacheItem<T>>;
      }>,
    ) => {
      const { pageKey, params } = action.payload;

      const existing = state[pageKey] as TableCacheItem<T> | undefined;

      const updated: TableCacheItem<T> = {
        ...(existing ?? {
          data: [],
          total: 0,
          search: '',
          page: 1,
          pageSize: 10,
          filter: '',
        }),
        ...params,
      };

      state[pageKey] = updated as TableCacheItem<unknown>;
    },

    clearTableCache: (state, action: PayloadAction<string>) => {
      delete state[action.payload];
    },

    clearAllCache: () => initialState,
  },
});

export const { setTableData, setTableParams, clearTableCache, clearAllCache } = tableCacheSlice.actions;

export default tableCacheSlice.reducer;
